import { useNavigate } from "react-router-dom";
import type { LatestEpisode } from "@/types/podcast";

// Downsize iTunes artwork URLs (e.g., /600x600bb.jpg → /200x200bb.jpg)
function getOptimizedArtwork(url: string): string {
  if (!url) return url;
  return url.replace(/\/\d+x\d+bb\.(jpg|png)$/i, "/200x200bb.$1");
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

interface LatestEpisodeCardProps {
  episode: LatestEpisode;
  compact?: boolean;
  showTranscribe?: boolean;
  onTranscribe?: (episode: LatestEpisode) => void;
  isTranscribing?: boolean;
  isTranscribed?: boolean;
}

export default function LatestEpisodeCard({ episode, compact, showTranscribe, onTranscribe, isTranscribing, isTranscribed }: LatestEpisodeCardProps) {
  const navigate = useNavigate();

  if (compact) {
    return (
      <div className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-all hover:shadow-md">
        {/* Transcribed checkmark badge */}
        {isTranscribed && (
          <div className="absolute top-2 left-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="white" className="h-3 w-3">
              <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        {/* Main clickable area */}
        <button
          onClick={() =>
            navigate(`/browse/podcast?feed=${encodeURIComponent(episode.feed_url)}`)
          }
          className="flex flex-col text-left w-full"
        >
          {/* Artwork - smaller for compact */}
          <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
            {episode.podcast_artwork_url ? (
              <img
                src={getOptimizedArtwork(episode.podcast_artwork_url)}
                alt={episode.podcast_name}
                className="h-full w-full object-cover"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  // Hide broken image and show placeholder
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`flex h-full w-full items-center justify-center text-gray-300 ${episode.podcast_artwork_url ? 'hidden absolute inset-0' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8">
                <path d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .456.122Z" />
              </svg>
            </div>
          </div>
          {/* Compact content */}
          <div className="flex flex-col p-2">
            <p className="text-xs font-medium text-gray-900 line-clamp-2 leading-tight">
              {episode.title}
            </p>
            <p className="mt-1 text-[10px] text-gray-500 truncate">
              {episode.podcast_name}
            </p>
          </div>
        </button>
        {/* Transcribe button overlay */}
        {showTranscribe && onTranscribe && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isTranscribing) onTranscribe(episode);
            }}
            disabled={isTranscribing}
            className={`absolute top-2 right-2 flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-white transition-opacity ${
              isTranscribing 
                ? 'bg-gray-400 opacity-100 cursor-not-allowed' 
                : 'bg-blue-600 opacity-0 group-hover:opacity-100 hover:bg-blue-700'
            }`}
          >
            {isTranscribing ? (
              <>
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                  <path d="M2 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3Zm2.5 1a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1Zm3.5.5A.5.5 0 0 1 8.5 4h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5ZM4.5 7a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1ZM8 7.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5Zm-3.5 3a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1Zm3.5.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5Z" />
                </svg>
                Transcribe
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() =>
        navigate(`/browse/podcast?feed=${encodeURIComponent(episode.feed_url)}`)
      }
      className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      {/* Transcribed checkmark badge */}
      {isTranscribed && (
        <div className="absolute top-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="white" className="h-3.5 w-3.5">
            <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      {/* Artwork */}
      <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
        {episode.podcast_artwork_url ? (
          <img
            src={getOptimizedArtwork(episode.podcast_artwork_url)}
            alt={episode.podcast_name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="eager"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`flex h-full w-full items-center justify-center text-gray-300 ${episode.podcast_artwork_url ? 'hidden absolute inset-0' : ''}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-12 w-12"
          >
            <path d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .456.122Z" />
          </svg>
        </div>
        {/* Gradient overlay at bottom of artwork */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-3.5">
        <p className="text-sm font-semibold text-gray-900 line-clamp-2">
          {episode.title}
        </p>

        {episode.description && (
          <p className="mt-1 text-xs text-gray-500 line-clamp-3">
            {episode.description.replace(/<[^>]*>/g, "")}
          </p>
        )}

        <div className="mt-auto flex items-center gap-1.5 pt-3 text-xs text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="h-3 w-3 shrink-0"
          >
            <path d="M7.557 2.066A.75.75 0 0 1 8 2.75v10.5a.75.75 0 0 1-1.248.56L3.59 11H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.59l3.162-2.81a.75.75 0 0 1 .805-.124ZM12.95 3.05a.75.75 0 1 0-1.06 1.06 5.5 5.5 0 0 1 0 7.78.75.75 0 1 0 1.06 1.06 7 7 0 0 0 0-9.9Z" />
            <path d="M10.828 5.172a.75.75 0 1 0-1.06 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 1 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
          </svg>
          <span className="truncate">{episode.podcast_name}</span>
          {episode.duration != null && episode.duration > 0 && (
            <>
              <span>·</span>
              <span className="shrink-0">
                {formatDuration(episode.duration)}
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}
